import { createContext, useContext, useState, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { themeColors, v2ThemeColors, v3ThemeColors, typography, spacing, shadows, borderRadius, applyThemeToRoot } from "./Theme";
import themeConfig from "./themeConfig";

// Create light theme based on our design system
export const lightTheme = createTheme({
	palette: {
		mode: "light",
		primary: {
			main: themeColors["--primary-main"],
			light: themeColors["--primary-light"],
			dark: themeColors["--primary-dark"],
			contrastText: themeColors["--primary-contrast"],
		},
		secondary: {
			main: themeColors["--secondary-main"],
			light: themeColors["--secondary-light"],
			dark: themeColors["--secondary-dark"],
			contrastText: themeColors["--secondary-contrast"],
		},
		error: {
			main: themeColors["--error"],
			light: themeColors["--error-light"],
		},
		warning: {
			main: themeColors["--warning"],
		},
		info: {
			main: themeColors["--info"],
		},
		success: {
			main: themeColors["--success"],
		},
		grey: {
			50: themeColors["--grey-50"],
			100: themeColors["--grey-100"],
			200: themeColors["--grey-200"],
			300: themeColors["--grey-300"],
			400: themeColors["--grey-400"],
			500: themeColors["--grey-500"],
			600: themeColors["--grey-600"],
			700: themeColors["--grey-700"],
			800: themeColors["--grey-800"],
			900: themeColors["--grey-900"],
		},
		text: {
			primary: themeColors["--text-primary"],
			secondary: themeColors["--text-secondary"],
			disabled: themeColors["--text-disabled"],
		},
		background: {
			default: themeColors["--background-default"],
			paper: themeColors["--background-paper"],
		},
		divider: themeColors["--divider"],
	},
	typography: {
		fontFamily: typography.fontFamily,
		fontWeightLight: typography.fontWeightLight,
		fontWeightRegular: typography.fontWeightRegular,
		fontWeightMedium: typography.fontWeightMedium,
		fontWeightBold: typography.fontWeightBold,
		h1: {
			fontSize: typography.h1.fontSize,
			fontWeight: typography.h1.fontWeight,
			lineHeight: typography.h1.lineHeight,
		},
		h2: {
			fontSize: typography.h2.fontSize,
			fontWeight: typography.h2.fontWeight,
			lineHeight: typography.h2.lineHeight,
		},
		h3: {
			fontSize: typography.h3.fontSize,
			fontWeight: typography.h3.fontWeight,
			lineHeight: typography.h3.lineHeight,
		},
		h4: {
			fontSize: typography.h4.fontSize,
			fontWeight: typography.h4.fontWeight,
			lineHeight: typography.h4.lineHeight,
		},
		h5: {
			fontSize: typography.h5.fontSize,
			fontWeight: typography.h5.fontWeight,
			lineHeight: typography.h5.lineHeight,
		},
		h6: {
			fontSize: typography.h6.fontSize,
			fontWeight: typography.h6.fontWeight,
			lineHeight: typography.h6.lineHeight,
		},
		body1: {
			fontSize: typography.body1.fontSize,
			fontWeight: typography.body1.fontWeight,
			lineHeight: typography.body1.lineHeight,
		},
		body2: {
			fontSize: typography.body2.fontSize,
			fontWeight: typography.body2.fontWeight,
			lineHeight: typography.body2.lineHeight,
		},
		caption: {
			fontSize: typography.caption.fontSize,
			fontWeight: typography.caption.fontWeight,
			lineHeight: typography.caption.lineHeight,
		},
		button: {
			fontSize: typography.button.fontSize,
			fontWeight: typography.button.fontWeight,
			lineHeight: typography.button.lineHeight,
		},
	},
	spacing: (factor) => {
		// Converting our spacing system to MUI's spacing system
		const spacingValues = {
			0: '0',
			1: spacing.xs,
			2: spacing.sm,
			3: spacing.md,
			4: spacing.lg,
			5: spacing.xl,
			6: spacing.xxl,
		};
		return spacingValues[factor] || `${factor * 8}px`;
	},
	shape: {
		borderRadius: parseInt(borderRadius.sm),
	},
	shadows: [
		'none', // shadows.none causes type error, so use literal 'none'
		shadows.xs,
		shadows.sm,
		shadows.sm,
		shadows.md,
		shadows.md,
		shadows.md,
		shadows.lg,
		shadows.lg,
		shadows.lg,
		shadows.lg,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
		shadows.xl,
	],
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: "none",
					borderRadius: borderRadius.sm,
					fontWeight: typography.fontWeightMedium,
				},
				contained: {
					boxShadow: shadows.sm,
					'&:hover': {
						boxShadow: shadows.md,
					},
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: borderRadius.md,
					boxShadow: shadows.sm,
					border: `1px solid ${themeColors["--divider"]}`,
				},
			},
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					padding: spacing.md,
					'&:last-child': {
						paddingBottom: spacing.md,
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-root': {
						borderRadius: borderRadius.sm,
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: themeColors["--primary-main"],
							borderWidth: '2px',
						},
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					padding: `${spacing.sm} ${spacing.md}`,
				},
				head: {
					fontWeight: typography.fontWeightBold,
					backgroundColor: themeColors["--grey-100"],
				},
			},
		},
	},
});

// Create a dark theme based on our design system (keeping for future use if needed)
export const darkTheme = createTheme({
	palette: {
		mode: "dark",
		primary: {
			main: themeColors["--primary-light"],
			light: themeColors["--primary-main"],
			dark: themeColors["--primary-dark"],
			contrastText: "#ffffff",
		},
		secondary: {
			main: themeColors["--secondary-light"],
			light: themeColors["--secondary-main"],
			dark: themeColors["--secondary-dark"],
			contrastText: "#ffffff",
		},
		error: {
			main: themeColors["--error"],
		},
		warning: {
			main: themeColors["--warning"],
		},
		info: {
			main: themeColors["--info"],
		},
		success: {
			main: themeColors["--success"],
		},
		background: {
			default: "#121212",
			paper: "#1e1e1e",
		},
		text: {
			primary: "#ffffff",
			secondary: "rgba(255, 255, 255, 0.7)",
			disabled: "rgba(255, 255, 255, 0.5)",
		},
		divider: "rgba(255, 255, 255, 0.12)",
	},
	// Reuse other properties from light theme
	typography: lightTheme.typography,
	spacing: lightTheme.spacing,
	shape: lightTheme.shape,
	shadows: lightTheme.shadows,
	components: {
		...lightTheme.components,
		MuiCard: {
			styleOverrides: {
				root: {
					backgroundColor: "#1e1e1e",
					borderRadius: borderRadius.md,
					boxShadow: shadows.md,
					border: "1px solid rgba(255, 255, 255, 0.12)",
				},
			},
		},
	},
});

// Create v2 theme based on our second design system
export const v2Theme = createTheme({
	palette: {
		mode: "light",
		primary: {
			main: v2ThemeColors["--primary-main"],
			light: v2ThemeColors["--primary-light"],
			dark: v2ThemeColors["--primary-dark"],
			contrastText: v2ThemeColors["--primary-contrast"],
		},
		secondary: {
			main: v2ThemeColors["--secondary-main"],
			light: v2ThemeColors["--secondary-light"],
			dark: v2ThemeColors["--secondary-dark"],
			contrastText: v2ThemeColors["--secondary-contrast"],
		},
		error: {
			main: v2ThemeColors["--error"],
			light: v2ThemeColors["--error-light"],
		},
		warning: {
			main: v2ThemeColors["--warning"],
		},
		info: {
			main: v2ThemeColors["--info"],
		},
		success: {
			main: v2ThemeColors["--success"],
		},
		grey: {
			50: v2ThemeColors["--grey-50"],
			100: v2ThemeColors["--grey-100"],
			200: v2ThemeColors["--grey-200"],
			300: v2ThemeColors["--grey-300"],
			400: v2ThemeColors["--grey-400"],
			500: v2ThemeColors["--grey-500"],
			600: v2ThemeColors["--grey-600"],
			700: v2ThemeColors["--grey-700"],
			800: v2ThemeColors["--grey-800"],
			900: v2ThemeColors["--grey-900"],
		},
		text: {
			primary: v2ThemeColors["--text-primary"],
			secondary: v2ThemeColors["--text-secondary"],
			disabled: v2ThemeColors["--text-disabled"],
		},
		background: {
			default: v2ThemeColors["--background-default"],
			paper: v2ThemeColors["--background-paper"],
		},
		divider: v2ThemeColors["--divider"],
	},
	// Reuse other properties from light theme since they're not color dependent
	typography: lightTheme.typography,
	spacing: lightTheme.spacing,
	shape: lightTheme.shape,
	shadows: lightTheme.shadows,
	components: {
		...lightTheme.components,
		MuiButton: {
			...lightTheme.components.MuiButton,
			styleOverrides: {
				...lightTheme.components.MuiButton.styleOverrides,
				contained: {
					boxShadow: shadows.sm,
					'&:hover': {
						boxShadow: shadows.md,
					},
				},
				outlined: {
					borderWidth: '2px',
				}
			},
		},
	},
});

// Create v3 theme — Förster Technik Smart Tag (placeholder palette, see Theme.js)
export const v3Theme = createTheme({
	palette: {
		mode: "light",
		primary: {
			main: v3ThemeColors["--primary-main"],
			light: v3ThemeColors["--primary-light"],
			dark: v3ThemeColors["--primary-dark"],
			contrastText: v3ThemeColors["--primary-contrast"],
		},
		secondary: {
			main: v3ThemeColors["--secondary-main"],
			light: v3ThemeColors["--secondary-light"],
			dark: v3ThemeColors["--secondary-dark"],
			contrastText: v3ThemeColors["--secondary-contrast"],
		},
		error: {
			main: v3ThemeColors["--error"],
			light: v3ThemeColors["--error-light"],
		},
		warning: {
			main: v3ThemeColors["--warning"],
		},
		info: {
			main: v3ThemeColors["--info"],
		},
		success: {
			main: v3ThemeColors["--success"],
		},
		grey: {
			50: v3ThemeColors["--grey-50"],
			100: v3ThemeColors["--grey-100"],
			200: v3ThemeColors["--grey-200"],
			300: v3ThemeColors["--grey-300"],
			400: v3ThemeColors["--grey-400"],
			500: v3ThemeColors["--grey-500"],
			600: v3ThemeColors["--grey-600"],
			700: v3ThemeColors["--grey-700"],
			800: v3ThemeColors["--grey-800"],
			900: v3ThemeColors["--grey-900"],
		},
		text: {
			primary: v3ThemeColors["--text-primary"],
			secondary: v3ThemeColors["--text-secondary"],
			disabled: v3ThemeColors["--text-disabled"],
		},
		background: {
			default: v3ThemeColors["--background-default"],
			paper: v3ThemeColors["--background-paper"],
		},
		divider: v3ThemeColors["--divider"],
	},
	// Reuse non-colour properties from the light theme
	typography: lightTheme.typography,
	spacing: lightTheme.spacing,
	shape: lightTheme.shape,
	shadows: lightTheme.shadows,
	components: lightTheme.components,
});

// Map a theme-version string to its MUI theme object
const resolveThemeObject = (version) =>
	version === "v2" ? v2Theme : version === "v3" ? v3Theme : lightTheme;

// Create theme context with default values
const ThemeContext = createContext({
	themeVersion: themeConfig.defaultTheme,
	toggleThemeVersion: () => {},
	theme: resolveThemeObject(themeConfig.defaultTheme),
	allowThemeSwitching: themeConfig.allowThemeSwitching,
});

// Custom hook to use the theme context
export const useAppTheme = () => useContext(ThemeContext);

// For backward compatibility
export const useTheme = useAppTheme;

// Theme provider component
export const ThemeProvider = ({ children }) => {
	// Get saved theme preference or use default from config
	const getSavedTheme = () => {
		if (themeConfig.persistThemePreference && themeConfig.allowThemeSwitching) {
			try {
				const savedTheme = localStorage.getItem(themeConfig.localStorageKey);
				return savedTheme === "v2" || savedTheme === "v3" || savedTheme === "default" ? savedTheme : themeConfig.defaultTheme;
			} catch (e) {
				console.warn("Could not access localStorage for theme preference", e);
				return themeConfig.defaultTheme;
			}
		}
		return themeConfig.defaultTheme;
	};

	// Initialize with saved theme or default from config
	const [themeVersion, setThemeVersion] = useState(getSavedTheme());
	
	// Function to toggle between the supported user-selectable themes.
	const toggleThemeVersion = () => {
		if (!themeConfig.allowThemeSwitching) return;
		
		setThemeVersion(prev => {
			const newTheme = prev === "default" ? "v2" : "default";
			
			// Save to localStorage if enabled
			if (themeConfig.persistThemePreference) {
				try {
					localStorage.setItem(themeConfig.localStorageKey, newTheme);
				} catch (e) {
					console.warn("Could not save theme preference to localStorage", e);
				}
			}
			
			return newTheme;
		});
	};
	
	// Effect to apply theme colors when component mounts or theme changes
	useEffect(() => {
		// Apply the appropriate theme colors to root based on version
		applyThemeToRoot(themeVersion);
	}, [themeVersion]);

	// Get the current theme based on version
	const currentTheme = resolveThemeObject(themeVersion);
	
	// Context value
	const value = {
		themeVersion,
		toggleThemeVersion,
		theme: currentTheme,
		allowThemeSwitching: themeConfig.allowThemeSwitching,
	};
	
	return (
		<ThemeContext.Provider value={value}>
			<MUIThemeProvider theme={currentTheme}>{children}</MUIThemeProvider>
		</ThemeContext.Provider>
	);
};
